// The terrain layer: the blocked mask, the plane it is prepared into, and
// ONE full-world quad that draws the floor, the void, the hachures, the
// rims, the ridge body and the contour rings from them (plan A.5 row 4,
// Part B §B.4; R432).
//
// Terrain is immutable inside a world, so the layer does no per-frame work
// at all. A hello hands it the mask; it writes the mask into the engine's
// data texture and then, off the frame, runs `prepareTerrain` — the field
// raster, the hole fill, the Euclidean distance transform and the component
// inradii, about three hundred milliseconds for the core world.
//
// **Until the prepare lands the layer draws the ANALYTIC program**, which
// sums the kernel at every screen pixel and needs nothing precomputed. The
// map therefore shows its rims and its fills from the first frame and gains
// its depth a moment later, exactly as it did before the plane carried the
// field. From then on the composite reads all four channels of the plane
// with one bilinear tap, and there is no flip to answer: a world's terrain
// never changes, so the plane is written once.
//
// The PixiJS reference submits ~1.35 M triangles a frame for this picture,
// and 70 % of the scene with it (R191). Here it is two.

import type { Engine } from '../engine';
import { DIST_STEP } from '../engine';
import type { DrawCtx, FrameInfo, Layer } from '../layer';
import type { WorldPass } from '../gfx/worldquad';
import type { DataTexture } from '../gfx/types';
import { UniformValues } from '../gfx/uniforms';
import {
  TERRAIN_BASE_UNIFORMS, TERRAIN_TEX_BASE_UNIFORMS, terrainProgram, terrainTexProgram,
} from '../shaders/terrain';
import { terrainStyleUniforms } from '../shaders/terrain-styles';
import { prepareTerrain, type TerrainPrep } from '../terrain-prep';
import { DEFAULT_KNOBS, onKnobs, type Knobs } from '../knobs';
import { HEX_SIZE } from '../../gen/balance';
import { AUTO_SCENE_LOOK } from '../../render/scene-look';
import type { TerrainStyle } from '../../ui/settings';
import { COLS, ROWS } from '../../world';

/**
 * Where the terrain plane sits, as a shader's three uniforms want it.
 *
 * The territory pass reads the same plane when `#rock=plane` asks it to, and
 * it must be told the same three figures. They are published here rather
 * than copied, because two placements cannot agree for ever (R432).
 */
export interface PlanePlacement {
  /** The world point of texel (0,0)'s low corner. */
  lo: readonly [number, number];
  /** `1/(texelPx·size)` on each axis: a world point times this is its uv. */
  uv: readonly [number, number];
  /** One texel in world px on each axis. */
  step: readonly [number, number];
}

/**
 * A list of four floats, whatever length the pack states.
 *
 * The water-lining uniforms are `float[4]` in GLSL, so a shorter list is
 * filled with zeroes — a line of zero alpha draws nothing — and a longer one
 * is cut. The pack's own validator keeps the three lists the same length,
 * so this only guards against a list that is not four long.
 */
function pad4(v: readonly number[]): number[] {
  const out = [0, 0, 0, 0];
  for (let i = 0; i < 4 && i < v.length; i++) out[i] = v[i];
  return out;
}

export class TerrainLayer implements Layer {
  readonly name = 'terrain';

  /**
   * Every program this layer compiled, by kind and by LOOK (R828).
   *
   * The constructor builds the pair of the look the scene opened in, which
   * for the shipping look is exactly the two programs the layer always
   * built. Every other look is compiled on the first frame that draws it
   * and kept, because a player who steps through the workshop comes back.
   */
  private readonly programs: Record<'analytic' | 'tex', Map<TerrainStyle, WorldPass>> = {
    analytic: new Map(), tex: new Map(),
  };
  /** One uniform set per look: the figures of its own picture. */
  private readonly styleU = new Map<TerrainStyle, UniformValues>();
  private readonly u = new UniformValues(TERRAIN_BASE_UNIFORMS);
  private readonly uTex = new UniformValues(TERRAIN_TEX_BASE_UNIFORMS);
  /** The two base sets, which take the plane, the lining and the tap together. */
  private readonly bases = [this.u, this.uTex] as const;
  private readonly stopKnobs: () => void;
  private shown = false;
  private knobs: Knobs = DEFAULT_KNOBS;
  /** The mask the last hello sent, kept for a re-prepare on a knob change. */
  private blockedBytes: Uint8Array | null = null;
  /** The plane, when the knob asked for a pitch the engine's is not. */
  private ownDist: DataTexture | null = null;
  /** Has a prepare filled the plane? The composite has nothing to read
   *  before it has, so the analytic program draws until then. */
  private prepared = false;
  /** The frame handle of a prepare that has not run yet. */
  private pending = 0;
  /** Where the last prepare put the plane. Null before the first one. */
  private placement: PlanePlacement | null = null;
  /**
   * The last prepare itself, kept for the chart furniture (R548).
   *
   * The plane goes to the GPU as a texture, and nothing reads a texture
   * back. Card 13 traces its isobaths on the CPU, off the same raster the
   * shader draws from, so the prepare is held rather than dropped: one
   * `Float32Array` for the world, replaced whole on a re-prepare.
   */
  private lastPrep: TerrainPrep | null = null;
  /** Is the water-lining of card 01 on? The shipping pixel is off. */
  private waterLines = false;
  /**
   * Which of the terrain looks the ground wears (themes.md §3.4, R814).
   *
   * The layer stores the id and draws the AUTO ground whatever it says.
   * A value builds a program of its own, so the read site is here and the
   * source builder lands with the value it belongs to.
   */
  private styleId: TerrainStyle = AUTO_SCENE_LOOK.terrain;

  constructor(private readonly engine: Engine, style?: TerrainStyle) {
    if (style) this.styleId = style;
    // The pair of the opening look, built here as they always were. A look
    // the player never picks costs no compile and no program.
    this.buildProgram('analytic');
    this.buildProgram('tex');
    this.stopKnobs = onKnobs((k) => this.applyKnobs(k));
  }

  /** One look's own uniform set, built once and kept. */
  private styleSet(id: TerrainStyle): UniformValues {
    let set = this.styleU.get(id);
    if (!set) {
      set = new UniformValues(terrainStyleUniforms(id));
      this.styleU.set(id, set);
      this.applyStyleKnobs(set);
    }
    return set;
  }

  /** The program of one kind for the look in force, compiled on demand. */
  private program(kind: 'analytic' | 'tex'): WorldPass {
    return this.programs[kind].get(this.styleId) ?? this.buildProgram(kind);
  }

  /** Compiles one program, wires its uniforms and caches it. */
  private buildProgram(kind: 'analytic' | 'tex'): WorldPass {
    const id = this.styleId;
    const style = this.styleSet(id);
    const analytic = kind === 'analytic';
    const base = analytic ? this.u : this.uTex;
    const pass = this.engine.newWorldPass(
      analytic ? terrainProgram(id) : terrainTexProgram(id));
    pass.setExtra((p) => { base.apply(p); style.apply(p); });
    this.programs[kind].set(id, pass);
    return pass;
  }

  /** The blocked mask of the running world: 0 open, 1 trench, 2 ridge. */
  get blocked(): Uint8Array | null { return this.blockedBytes; }

  /** Is the composite reading the terrain plane's own fields? */
  get usesFieldTexture(): boolean {
    return this.knobs.terrain === 'tex' && this.prepared;
  }

  /** The composite's own uniform values, for the tests. */
  get texValues(): UniformValues { return this.uTex; }

  /** The look in force, and its figures (R828). */
  get styleKey(): TerrainStyle { return this.styleId; }

  get styleValues(): UniformValues { return this.styleSet(this.styleId); }

  /**
   * Where the plane sits, or null while a prepare has not landed.
   *
   * It does NOT ask which program draws: the prepare runs whatever the
   * `#terrain` knob says, so the plane is there for the territory pass to
   * read even where the terrain pass sums its own kernel.
   */
  get planePlacement(): PlanePlacement | null {
    return this.prepared ? this.placement : null;
  }

  /**
   * The terrain plane itself, or null while a prepare has not landed
   * (R548, §17.3.13).
   *
   * The chart traces the isobaths of a trench off this raster, so it is the
   * SAME field the composite draws the ground from and the two can never
   * disagree about where a contour runs.
   */
  get plane(): TerrainPrep | null {
    return this.prepared ? this.lastPrep : null;
  }

  /**
   * The water-lining of card 01 (R548, §17.3.01).
   *
   * The chart piece owns no batch: it writes these uniforms and draws
   * nothing. `on` false is the shipping pixel, so a reader who leaves the
   * furniture off or in the modern reading compiles and runs exactly the
   * program the client ran before the chart landed.
   */
  setWaterLines(
    on: boolean,
    cfg?: { d: readonly number[]; alpha: readonly number[]; width: readonly number[]; earlyOutPx: number },
  ): void {
    if (on === this.waterLines && !cfg) return;
    this.waterLines = on;
    for (const set of this.bases) {
      set.set('uWaterLines', on ? 1 : 0);
      if (!cfg) continue;
      set.set('uLineD', pad4(cfg.d));
      set.set('uLineA', pad4(cfg.alpha));
      set.set('uLineW', pad4(cfg.width));
      set.set('uLineOut', cfg.earlyOutPx);
    }
  }

  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /** The look of the ridges and the trenches in force. */
  get style(): TerrainStyle { return this.styleId; }

  /**
   * Sets the look of the ridges and the trenches (R814, R828).
   *
   * It compiles nothing: the look's uniform set is armed here, and the
   * next `draw` fetches or builds the program. A look change is a theme
   * change, so it happens once a session and never in a frame loop.
   */
  setStyle(id: TerrainStyle): void {
    if (id === this.styleId) return;
    this.styleId = id;
    this.styleSet(id);
  }

  /**
   * The hello's mask.
   *
   * The mask itself reaches the shader at once, because the analytic fills
   * and rims are a field over it and need nothing precomputed. The plane
   * waits two animation frames, so the first paint of a world is not the
   * frame that pays for it.
   */
  setBlocked(bytes: Uint8Array): void {
    this.blockedBytes = bytes;
    this.engine.blocked.set(bytes);
    this.shown = true;
    this.schedulePrepare();
  }

  update(_frame: FrameInfo): void {
    // Terrain never moves. The one thing a frame may owe is the upload of a
    // knob-sized plane, which the engine's own sweep does not know about;
    // `upload` returns at once when the plane is clean.
    this.ownDist?.upload();
  }

  draw(ctx: DrawCtx): void {
    this.program(this.usesFieldTexture ? 'tex' : 'analytic').draw(ctx.cam, this.engine.env);
  }

  dispose(): void {
    this.cancelPrepare();
    this.stopKnobs();
    this.releaseOwnDist();
    for (const cache of Object.values(this.programs)) {
      for (const pass of cache.values()) pass.dispose();
      cache.clear();
    }
  }

  // -- the plane -----------------------------------------------------------

  private schedulePrepare(): void {
    this.cancelPrepare();
    this.prepared = false;
    if (typeof requestAnimationFrame !== 'function') {
      this.prepare();
      return;
    }
    // Two frames: the first one presents the world, the second one is the
    // callback that hands the prepare to the frame after it. A `setTimeout`
    // would do as well; two frames say plainly which paint must not pay.
    this.pending = requestAnimationFrame(() => {
      this.pending = requestAnimationFrame(() => {
        this.pending = 0;
        this.prepare();
      });
    });
  }

  private cancelPrepare(): void {
    if (this.pending !== 0 && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.pending);
    }
    this.pending = 0;
  }

  private prepare(): void {
    const bytes = this.blockedBytes;
    if (!bytes) return;
    const texelPx = this.knobs.edt;
    const prep = prepareTerrain(bytes, COLS, ROWS, {
      texelPx,
      // The raster and the shader must be the SAME curve, so the field
      // radius is one figure and the knob moves both halves of it.
      radiusT: this.knobs.tr * HEX_SIZE,
      // The plane covers the whole rectangle the world quad spans.
      pad: this.engine.env.pad,
    });
    this.upload(prep);
  }

  private upload(prep: TerrainPrep): void {
    const dist = this.planeTextureFor(prep);
    dist.set(prep.plane);
    this.engine.compR.set(prep.compR);
    this.writePlacement(prep);
    this.lastPrep = prep;
    this.prepared = true;
  }

  /** Tells both programs where the plane sits and how coarse it is. */
  private writePlacement(prep: TerrainPrep): void {
    const place: PlanePlacement = {
      lo: [prep.loX, prep.loY],
      uv: [1 / (prep.texelPx * prep.distW), 1 / (prep.texelPx * prep.distH)],
      step: [prep.texelPx, prep.texelPx],
    };
    this.placement = place;
    for (const set of this.bases) {
      set.set('uDistLo', place.lo);
      set.set('uDistUv', place.uv);
      set.set('uDistStep', place.step);
    }
  }

  /**
   * The plane the prepare goes into.
   *
   * The engine sizes its own at `DIST_STEP`, which is the shipping pitch, so
   * the normal path uses it and allocates nothing. `#edt=2` asks for a
   * quarter of the texel pitch and four times the plane, and that one is the
   * layer's: it is made here, pointed at from the world environment, and
   * uploaded by the layer rather than by the engine's own sweep.
   */
  private planeTextureFor(prep: TerrainPrep): DataTexture {
    const engineDist = this.engine.dist;
    if (prep.texelPx === DIST_STEP
      && prep.distW === engineDist.width && prep.distH === engineDist.height) {
      this.releaseOwnDist();
      this.engine.env.textures.dist = engineDist;
      return engineDist;
    }
    if (!this.ownDist || this.ownDist.width !== prep.distW || this.ownDist.height !== prep.distH) {
      this.releaseOwnDist();
      this.ownDist = this.engine.newDataTexture(
        'dist-knob', prep.distW, prep.distH, 'rgba16f', true,
      );
    }
    this.engine.env.textures.dist = this.ownDist;
    return this.ownDist;
  }

  private releaseOwnDist(): void {
    if (!this.ownDist) return;
    this.ownDist.dispose();
    this.ownDist = null;
    this.engine.env.textures.dist = this.engine.dist;
  }

  // -- the knobs -----------------------------------------------------------

  private applyKnobs(k: Knobs): void {
    const before = this.knobs;
    this.knobs = k;
    this.u.set('uRadiusT', k.tr * HEX_SIZE);
    for (const set of this.bases) set.set('uDistTap', k.dtap === 'bspline' ? 1 : 0);
    for (const set of this.styleU.values()) this.applyStyleKnobs(set);
    // Only the composite has a plane to ask, so the analytic program keeps
    // the seven-fetch scan whatever `#early` says.
    this.uTex.set('uEarlyPlane', k.early === 'plane' ? 1 : 0);
    // The raster is a function of the radius and the pitch, so those two
    // knobs are the only ones that cost a re-prepare.
    if (this.blockedBytes && (before.tr !== k.tr || before.edt !== k.edt)) {
      this.schedulePrepare();
    }
  }

  /**
   * The three knobs that tune ONE look's own figures.
   *
   * The ring step, the hachure switch and the tick scale belong to the
   * `default` picture and to no other, so each one is written where the
   * look declares it. A look that draws no ring and no tick simply does
   * not carry the name (R828).
   */
  private applyStyleKnobs(set: UniformValues): void {
    const k = this.knobs;
    if (set.has('uRingStep')) set.set('uRingStep', k.ringstep);
    if (set.has('uHachureMode')) set.set('uHachureMode', k.hach);
    if (set.has('uTickScale')) set.set('uTickScale', k.tick);
  }
}
